import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'

/**
 * PWA Web Push subscription lifecycle.
 *
 * States:
 *  - 'unsupported'  — browser doesn't expose PushManager / Notification / SW
 *  - 'unconfigured' — server has no VAPID public key (Luis hasn't set env)
 *  - 'denied'       — user or OS blocked notifications permanently
 *  - 'default'      — permission never requested / pending
 *  - 'subscribed'   — user is subscribed on this device
 *
 * Safe to import on desktop; returns 'unsupported' on browsers without
 * the APIs instead of throwing at module load. The Settings toggle
 * calls `subscribe()` / `unsubscribe()` / `sendTest()` directly.
 */

export type PushStatus =
  | 'unsupported'
  | 'unconfigured'
  | 'denied'
  | 'default'
  | 'subscribed'

/** Convert a base64url string (VAPID public key) → Uint8Array backed by a
 *  real ArrayBuffer. PushManager.subscribe's `applicationServerKey` is
 *  typed as `BufferSource`, which in TypeScript 5.x rules out the default
 *  `Uint8Array<ArrayBufferLike>` (can be SharedArrayBuffer). Allocating
 *  the ArrayBuffer explicitly pins the generic to `Uint8Array<ArrayBuffer>`. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(b64)
  const buffer = new ArrayBuffer(raw.length)
  const out = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function isBrowserSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** ArrayBuffer → base64url (for PushSubscription keys sent to backend). */
function bufferToBase64Url(buffer: ArrayBuffer | null | undefined): string {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return window
    .btoa(bin)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export function usePushNotifications() {
  const [status, setStatus] = useState<PushStatus>('default')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [serverConfigured, setServerConfigured] = useState<boolean | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)

    if (!isBrowserSupported()) {
      setStatus('unsupported')
      setLoading(false)
      return
    }

    try {
      const { public_key, configured } = await api.get<{
        public_key: string | null
        configured: boolean
      }>('/push/public-key')
      setServerConfigured(configured)

      if (!configured || !public_key) {
        setStatus('unconfigured')
        setLoading(false)
        return
      }

      if (Notification.permission === 'denied') {
        setStatus('denied')
        setLoading(false)
        return
      }

      const reg = await navigator.serviceWorker.ready
      const existing = await reg.pushManager.getSubscription()
      if (existing) {
        setStatus('subscribed')
      } else {
        setStatus('default')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to read push status')
      setStatus('default')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const subscribe = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      if (!isBrowserSupported()) throw new Error('Browser sem suporte a push')

      const { public_key } = await api.get<{
        public_key: string | null
        configured: boolean
      }>('/push/public-key')
      if (!public_key) throw new Error('Servidor sem VAPID configurado')

      const perm = await Notification.requestPermission()
      if (perm === 'denied') {
        setStatus('denied')
        throw new Error(
          'Permissão bloqueada. Permite nas definições do browser/sistema.'
        )
      }
      if (perm !== 'granted') {
        setStatus('default')
        throw new Error('Permissão não concedida')
      }

      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      })

      const json = sub.toJSON() as {
        endpoint?: string
        keys?: { p256dh?: string; auth?: string }
      }
      await api.post('/push/subscribe', {
        endpoint: json.endpoint || sub.endpoint,
        keys: {
          // Fallback to raw buffers if toJSON() doesn't populate
          // (some older browsers — getKey() is standard across all supported)
          p256dh:
            json.keys?.p256dh ||
            bufferToBase64Url(sub.getKey('p256dh') as ArrayBuffer | null),
          auth:
            json.keys?.auth ||
            bufferToBase64Url(sub.getKey('auth') as ArrayBuffer | null),
        },
      })

      setStatus('subscribed')
    } catch (e: any) {
      setError(e?.message || 'Falha a subscrever')
      // Don't force status=denied unless Notification.permission says so —
      // temporary network errors shouldn't look like a permanent block.
    } finally {
      setLoading(false)
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        const endpoint = sub.endpoint
        await sub.unsubscribe()
        await api
          .post('/push/unsubscribe', { endpoint })
          .catch(() => undefined) // server-side cleanup; ignore remote failure
      }
      setStatus('default')
    } catch (e: any) {
      setError(e?.message || 'Falha a anular subscrição')
    } finally {
      setLoading(false)
    }
  }, [])

  const sendTest = useCallback(async () => {
    setError(null)
    try {
      return await api.post<{ sent: number; failed: number; deleted: number }>(
        '/push/test'
      )
    } catch (e: any) {
      setError(e?.message || 'Falha a enviar teste')
      throw e
    }
  }, [])

  return {
    status,
    loading,
    error,
    serverConfigured,
    subscribe,
    unsubscribe,
    sendTest,
    refresh,
  }
}
